import config from '../config';
import ApiService from './ApiService';
import * as XLSX from 'xlsx';

/**
 * Converts a params object to a URL query string, handling arrays.
 */
function buildQueryString(params = {}) {
  const parts = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== null && v !== undefined && v !== '') {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
        }
      });
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

async function fetchJson(endpoint) {
  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    headers: await ApiService.getDefaultHeaders(),
  });
  if (!response.ok) {
    throw new Error(`API Error ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

class DashboardAnalyticsService {
  /**
   * GET /api/dashboard/analytics/summary
   * params: { odm_type, year, start_date, end_date }
   */
  static getSummary(params = {}) {
    return fetchJson(`/api/dashboard/analytics/summary${buildQueryString(params)}`);
  }

  /**
   * GET /api/dashboard/analytics/filter-options
   */
  static getFilterOptions() {
    return fetchJson('/api/dashboard/analytics/filter-options');
  }

  /**
   * GET /api/dashboard/analytics/volume-by-request-status
   * params: { year, quarter, odm_type, start_date, end_date }
   */
  static getVolumeByRequestStatus(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/volume-by-request-status${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/volume-by-approval-status
   * params: { year, quarter, odm_type, start_date, end_date }
   */
  static getVolumeByApprovalStatus(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/volume-by-approval-status${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/volume-by-type
   * params: { years: [], include_wip, start_date, end_date }
   */
  static getVolumeByType(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/volume-by-type${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/wip-summary
   * params: { odm_type, year, start_date, end_date }
   */
  static getWipSummary(params = {}) {
    return fetchJson(`/api/dashboard/analytics/wip-summary${buildQueryString(params)}`);
  }

  /**
   * GET /api/dashboard/analytics/wip-totals
   * params: { odm_type, years: [] }
   */
  static getWipTotals(params = {}) {
    return fetchJson(`/api/dashboard/analytics/wip-totals${buildQueryString(params)}`);
  }

  /**
   * GET /api/dashboard/analytics/wip-by-phase
   * params: { year, odm_type, start_date, end_date }
   */
  static getWipByPhase(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/wip-by-phase${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/stage-cycle-time
   * params: { odm_type, year, start_date, end_date, status, grm_demand_number }
   * status: 'closed' | 'open' | omit for no filter.
   * grm_demand_number: array of exact demand numbers, or omit for no filter.
   */
  static getStageCycleTime(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/stage-cycle-time${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/stage-cycle-time/detail
   * params: { odm_type, year, start_date, end_date, variant, status, grm_demand_number }
   * status: 'closed' | 'open' | omit for no filter.
   * grm_demand_number: array of exact demand numbers, or omit for no filter.
   * One row PER REQUEST (wide format) -- demand number, request identity, and
   * for every stage a Start/End/Days column trio. The per-request evidence
   * behind /analytics/stage-cycle-time's rollup; feeds the "Data View" export's
   * second sheet, not the chart itself.
   */
  static getStageCycleTimeDetail(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/stage-cycle-time/detail${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/bandwidth-demand-numbers
   * params: { odm_type, year, start_date, end_date, status }
   * Feeds the GRM Number filter's own picker -- [{ request_id,
   * grm_demand_number, title, request_state }], every bandwidth request with a
   * non-blank demand number.
   */
  static getBandwidthDemandNumbers(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/bandwidth-demand-numbers${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/phase-cycle-time
   * params: { odm_type, year, start_date, end_date, variant, status, grm_demand_number }
   * variant: 'soft' | 'hard' | 'non_standard' | 'all' (default 'soft').
   * status: 'closed' | 'open' | omit for no filter.
   * grm_demand_number: array of exact demand numbers, or omit for no filter.
   * Returns an object, not an array: { variant, variant_label, variant_counts,
   * requests_in_variant, phases: [...], notes: [...] }.
   */
  static getPhaseCycleTime(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/phase-cycle-time${buildQueryString(params)}`
    );
  }

  /**
   * GET /api/dashboard/analytics/data-view
   * params: { view_type, odm_type, year, start_date, end_date, page, page_size }
   */
  static getDataView(params = {}) {
    return fetchJson(
      `/api/dashboard/analytics/data-view${buildQueryString(params)}`
    );
  }

  /**
   * Triggers a browser download for data as CSV or XLSX.
   * @param {Array} data - Array of objects to export
   * @param {string} filename - Base filename (without extension)
   * @param {'csv'|'xlsx'} format - Export format
   * @param {Array<{name: string, rows: Array}>} extraSheets - additional XLSX
   *        sheets appended after the primary 'Data' sheet. XLSX-only: a CSV
   *        file has no concept of multiple sheets, so these are silently
   *        dropped for format === 'csv' rather than raising an error.
   */
  static downloadExport(data, filename, format = 'csv', extraSheets = []) {
    if (!data || data.length === 0) return;

    if (format === 'xlsx') {
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Data');
      extraSheets.forEach(({ name, rows }) => {
        if (!rows || rows.length === 0) return;
        // Excel caps sheet names at 31 characters.
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), name.slice(0, 31));
      });
      XLSX.writeFile(workbook, `${filename}.xlsx`);
    } else {
      // CSV
      const headers = Object.keys(data[0]);
      const csvRows = [
        headers.join(','),
        ...data.map((row) =>
          headers
            .map((h) => {
              const val = row[h] === null || row[h] === undefined ? '' : String(row[h]);
              // Escape values containing commas, quotes, or newlines
              if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                return `"${val.replace(/"/g, '""')}"`;
              }
              return val;
            })
            .join(',')
        ),
      ];
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}

export default DashboardAnalyticsService;
